import type React from "react";
import { useState } from "react";

import { translate } from "app/filters/i18nFilter";
import type { IFilterViewBaseModelProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";

import type { IAppsIosIdentifiersDescriptionDto } from "../../../../types/descriptionDto";
import type { IAppsIosIdentifierFilter } from "../../../../types/iosFiltersDto";
import AppIdentifierBulkAdd from "../AppIdentifierBulkAdd/AppIdentifierBulkAdd";
import { StyledFilterHeading } from "../styles";
import { TextField } from "../TextField/TextField";
import type { IosIdentifierType } from "../types";
import { iosIdentifierTypeToAppsIdentifiers, parseInputValue } from "../utils";

import { IosIdentifierDropdown } from "./IosIdentifierDropdown/IosIdentifierDropdown";
import { StyledDropdownsFlexContainer } from "./styles";

const regExpByIdentifierType = {
    trackIds: /^[0-9]+$/,
    bundleIds: /^[a-zA-Z]([a-zA-Z0-9]*\.)+[a-zA-Z0-9]+$/,
};

export const AppsIosIdentifiersFilter: React.FC<
    IFilterViewBaseModelProps<IAppsIosIdentifierFilter, IAppsIosIdentifiersDescriptionDto>
> = ({ filter, value, onFilterUpdate }) => {
    const [identifierType, setIdentifierType] = useState<IosIdentifierType>("trackIds" as IosIdentifierType);

    const identifierOptions = filter.options.identifiers as IosIdentifierType[];

    const handleUpdate = (newValue: string[]) => {
        const updatedValue = {
            ...value,
            [identifierType]: Array.from(new Set([...(value?.[identifierType] ?? []), ...newValue])),
        };
        onFilterUpdate({ trackIds: updatedValue.trackIds ?? [], bundleIds: updatedValue.bundleIds ?? [] });
    };

    const handleTextFieldUpdate = (newValue: string) => {
        handleUpdate(parseInputValue(newValue));
    };

    const validate = (inputValue: string) => {
        const regExp = regExpByIdentifierType[identifierType];
        return parseInputValue(inputValue).every((v) => regExp.test(v));
    };

    return (
        <div>
            <StyledFilterHeading>{translate("si.find_apps.filters.identifier.ios.title")}</StyledFilterHeading>
            <StyledDropdownsFlexContainer>
                <IosIdentifierDropdown
                    value={identifierType}
                    onUpdate={setIdentifierType}
                    options={identifierOptions}
                />
                <TextField
                    onUpdate={handleTextFieldUpdate}
                    placeholder={translate("si.find_apps.filters.identifier.ios.placeholder", {
                        entity: iosIdentifierTypeToAppsIdentifiers[identifierType],
                    })}
                    label={translate("si.find_apps.filters.identifier.ios.label")}
                    validate={validate}
                />
            </StyledDropdownsFlexContainer>
            <AppIdentifierBulkAdd
                onUpdate={handleUpdate}
                appIdentifierType={iosIdentifierTypeToAppsIdentifiers[identifierType]}
            />
        </div>
    );
};
